<template>
  <StatsWidget
    :title="$t('stats_dashboard.margin_products.title')"
    :description="$t('stats_dashboard.margin_products.description')"
    icon="bullseye"
    tone="violet"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasData"
    :span-full="true"
    :empty-icon="'bullseye'"
    :empty-message="$t('stats_dashboard.margin_products.empty')"
    @refresh="$emit('refresh')"
  >
    <div class="stats-margin-products">
      <!-- Totals strip — feels like the same "summary above the
           detail" pattern as StatsProducts so the page reads cohesively. -->
      <div class="stats-margin-products__totals">
        <div class="stats-margin-products__total-card">
          <span class="stats-margin-products__total-label">
            {{ $t('stats_dashboard.margin_products.totals.product_count') }}
          </span>
          <span class="stats-margin-products__total-value">
            {{ formatInteger(totals.product_count) }}
          </span>
        </div>
        <div class="stats-margin-products__total-card stats-margin-products__total-card--revenue">
          <span class="stats-margin-products__total-label">
            {{ $t('stats_dashboard.margin_products.totals.revenue') }}
          </span>
          <span class="stats-margin-products__total-value">
            {{ formatCurrency(totals.revenue) }}
          </span>
        </div>
        <div class="stats-margin-products__total-card stats-margin-products__total-card--margin">
          <span class="stats-margin-products__total-label">
            {{ $t('stats_dashboard.margin_products.totals.margin') }}
          </span>
          <span class="stats-margin-products__total-value">
            {{ formatCurrency(totals.margin_value) }}
            <span class="stats-margin-products__total-pct">
              ({{ formatPercent(totals.margin_pct) }})
            </span>
          </span>
        </div>
      </div>

      <!-- Two ranked tables: best vs. worst marge -->
      <div class="stats-margin-products__grid">
        <section v-for="board in boards" :key="board.key" class="stats-margin-products__panel">
          <header class="stats-margin-products__panel-head">
            <div class="stats-margin-products__panel-title-group">
              <span
                class="stats-margin-products__panel-icon"
                :class="`stats-margin-products__panel-icon--${board.tone}`"
              >
                <BaseIcon :name="board.icon" size="12" />
              </span>
              <h4 class="stats-margin-products__panel-title">
                {{ board.title }}
              </h4>
            </div>
            <span class="stats-margin-products__panel-hint">
              {{ board.hint }}
            </span>
          </header>

          <ul v-if="board.rows.length" class="stats-margin-products__list">
            <li
              v-for="(row, idx) in board.rows"
              :key="row.product_id || `${board.key}-${idx}`"
              class="stats-margin-products__row"
              :class="{
                'stats-margin-products__row--flagged': row.under_threshold,
              }"
            >
              <span class="stats-margin-products__rank">{{ idx + 1 }}</span>

              <div class="stats-margin-products__product">
                <div class="stats-margin-products__product-head">
                  <span class="stats-margin-products__product-name">
                    {{ row.title || $t('stats_dashboard.margin_products.unknown_product') }}
                  </span>
                  <span class="stats-margin-products__margin-pct" :class="marginPctClass(row)">
                    {{ formatPercent(row.margin_pct) }}
                  </span>
                </div>

                <div class="stats-margin-products__product-meta">
                  <span class="stats-margin-products__meta-item">
                    {{
                      $t('stats_dashboard.margin_products.meta.revenue', {
                        value: formatCurrency(row.revenue),
                      })
                    }}
                  </span>
                  <span class="stats-margin-products__meta-sep">·</span>
                  <span class="stats-margin-products__meta-item">
                    {{
                      $t('stats_dashboard.margin_products.meta.cost', {
                        value: formatCurrency(row.cost),
                      })
                    }}
                  </span>
                  <span class="stats-margin-products__meta-sep">·</span>
                  <span class="stats-margin-products__meta-item">
                    {{
                      $t('stats_dashboard.margin_products.meta.units', {
                        n: formatInteger(row.units),
                      })
                    }}
                  </span>
                  <span v-if="row.under_threshold" class="stats-margin-products__flag-badge">
                    <BaseIcon name="exclamation-triangle" size="10" />
                    {{
                      $t('stats_dashboard.margin_products.flag_below_min', {
                        min: formatPercent(row.min_margin_pct),
                      })
                    }}
                  </span>
                </div>
              </div>
            </li>
          </ul>

          <p v-else class="stats-margin-products__empty-tab">
            {{ board.emptyLabel }}
          </p>
        </section>
      </div>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Per-product marge widget (B in the 2026-05-12 margin delta).
   *
   * Renders best-N and worst-N products side-by-side. The backend
   * already filters out products without revenue or without snapshot
   * lines, so we don't have to do any extra cleanup here.
   *
   * The margin-percentage cell is colour-coded:
   *   • green  — at or above the per-product target (or >= 30% as a
   *              fallback when no target is set)
   *   • amber  — within 5pp under min_margin_pct
   *   • red    — under_threshold = true (or margin_pct < 0)
   */
  import { computed, getCurrentInstance } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { formatCurrency, formatInteger, formatPercent } from '@/utils/statsFormat';

  // When the product has no explicit min_margin_pct, we fall back to
  // a heuristic threshold for the colour scale so the green/amber/red
  // gradient remains meaningful. 30% is a sensible all-trades default
  // for installation businesses; can be tuned later.
  const FALLBACK_GREEN_THRESHOLD = 30;
  const AMBER_BAND_PP = 5;

  export default {
    name: 'StatsMarginProducts',

    components: { StatsWidget, BaseIcon },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const best = computed(() => props.widget.data?.best || []);
      const worst = computed(() => props.widget.data?.worst || []);
      const totals = computed(() => props.widget.data?.totals || {});

      const hasData = computed(() => best.value.length > 0 || worst.value.length > 0);

      const boards = computed(() => [
        {
          key: 'best',
          title: $t('stats_dashboard.margin_products.best_title'),
          hint: $t('stats_dashboard.margin_products.best_hint'),
          icon: 'check-circle',
          tone: 'green',
          rows: best.value,
          emptyLabel: $t('stats_dashboard.margin_products.best_empty'),
        },
        {
          key: 'worst',
          title: $t('stats_dashboard.margin_products.worst_title'),
          hint: $t('stats_dashboard.margin_products.worst_hint'),
          icon: 'exclamation-triangle',
          tone: 'rose',
          rows: worst.value,
          emptyLabel: $t('stats_dashboard.margin_products.worst_empty'),
        },
      ]);

      const marginPctClass = (row) => {
        const pct = Number(row.margin_pct) || 0;
        const min = Number(row.min_margin_pct) || 0;
        if (row.under_threshold || pct < 0) {
          return 'stats-margin-products__margin-pct--red';
        }
        if (min > 0) {
          // Amber band: just above the floor.
          if (pct < min + AMBER_BAND_PP) {
            return 'stats-margin-products__margin-pct--amber';
          }
          return 'stats-margin-products__margin-pct--green';
        }
        // No explicit target — use heuristic colour scale.
        if (pct >= FALLBACK_GREEN_THRESHOLD) {
          return 'stats-margin-products__margin-pct--green';
        }
        if (pct >= FALLBACK_GREEN_THRESHOLD - AMBER_BAND_PP) {
          return 'stats-margin-products__margin-pct--amber';
        }
        return 'stats-margin-products__margin-pct--red';
      };

      return {
        best,
        worst,
        totals,
        boards,
        hasData,
        marginPctClass,
        formatCurrency,
        formatInteger,
        formatPercent,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-margin-products {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
    min-height: 0;

    // ---------- Totals strip ----------

    &__totals {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 0.625rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__total-card {
      padding: 0.625rem 0.875rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-100);
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;

      &--revenue {
        background: linear-gradient(180deg, #eff6ff, #f8fafc);
        border-color: #bfdbfe;
      }

      &--margin {
        background: linear-gradient(180deg, #ecfdf5, #f8fafc);
        border-color: #bbf7d0;
      }
    }

    &__total-label {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      @include ellipsis(1);
    }

    &__total-value {
      font-size: 1.125rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
      @include ellipsis(1);
    }

    &__total-pct {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-500);
      margin-left: 0.25rem;
    }

    // ---------- Best/worst grid ----------

    &__grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 0.875rem;

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }

    &__panel {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.875rem;
      border: 1px solid var(--slate-100);
      border-radius: 0.75rem;
      background: var(--white);
      min-height: 8rem;
    }

    &__panel-head {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 0.5rem;
    }

    &__panel-title-group {
      display: inline-flex;
      align-items: center;
      gap: 0.4375rem;
      min-width: 0;
    }

    &__panel-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 0.375rem;
      flex-shrink: 0;

      &--green {
        background: var(--green-50, #ecfdf5);
        color: var(--green-600);
      }

      &--rose {
        background: var(--rose-50, #fff1f2);
        color: var(--rose-600, #e11d48);
      }
    }

    &__panel-title {
      margin: 0;
      font-size: 0.8125rem;
      font-weight: 700;
      color: var(--slate-800);
      @include ellipsis(1);
    }

    &__panel-hint {
      font-size: 0.6875rem;
      color: var(--slate-500);
      @include ellipsis(1);
    }

    // ---------- List ----------

    &__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    &__row {
      display: grid;
      grid-template-columns: 1.5rem 1fr;
      gap: 0.625rem;
      padding: 0.5rem 0.625rem;
      border: 1px solid var(--slate-100);
      border-radius: 0.5rem;
      transition: border-color 0.15s ease;

      @include hover {
        border-color: var(--slate-200);
      }

      &--flagged {
        background: var(--rose-50, #fff1f2);
        border-color: rgba(244, 63, 94, 0.2);
      }
    }

    &__rank {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 50%;
      background: var(--slate-100);
      color: var(--slate-700);
      font-size: 0.6875rem;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;
      align-self: flex-start;
      margin-top: 0.0625rem;
    }

    &__product {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
    }

    &__product-head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__product-name {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-900);
      @include ellipsis(1);
    }

    &__margin-pct {
      font-size: 0.8125rem;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      padding: 0.0625rem 0.4375rem;
      border-radius: 9999px;
      flex-shrink: 0;

      &--green {
        background: var(--green-50, #ecfdf5);
        color: var(--green-700, #047857);
      }
      &--amber {
        background: var(--amber-50, #fffbeb);
        color: var(--amber-700, #b45309);
      }
      &--red {
        background: var(--rose-50, #fff1f2);
        color: var(--rose-700, #be123c);
      }
    }

    &__product-meta {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem 0.375rem;
      font-size: 0.6875rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
    }

    &__meta-sep {
      color: var(--slate-300);
    }

    &__flag-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.0625rem 0.4375rem;
      background: rgba(244, 63, 94, 0.1);
      color: var(--rose-700, #be123c);
      border-radius: 9999px;
      font-size: 0.625rem;
      font-weight: 600;
      margin-left: 0.25rem;
    }

    &__empty-tab {
      margin: 0;
      padding: 1.25rem;
      text-align: center;
      color: var(--slate-500);
      font-size: 0.75rem;
      background: var(--slate-50);
      border: 1px dashed var(--slate-200);
      border-radius: 0.5rem;
    }
  }
</style>
